import { useState, useRef, useCallback, useEffect } from 'react';
import { SpinState, WheelEntry } from '../types';
import { secureRandomInt } from '../utils/random';
import {
  calculateTargetRotation,
  easeOutCustom,
  easeOutCustomVelocity,
  getIndexUnderPointer,
} from '../utils/wheelMath';

interface UseSpinControllerProps {
  entries: WheelEntry[];
  duration: number; // in seconds
  onWinnerSelected?: (winner: WheelEntry, index: number) => void;
  onTick?: (velocityRatio: number) => void;
  onSpinStart?: () => void;
  onStop?: () => void;
}

export function useSpinController({
  entries,
  duration,
  onWinnerSelected,
  onTick,
  onSpinStart,
  onStop,
}: UseSpinControllerProps) {
  const [spinState, setSpinState] = useState<SpinState>('idle');
  const [rotationDeg, setRotationDeg] = useState<number>(0);
  const [pointerDeflectionDeg, setPointerDeflectionDeg] = useState<number>(0);
  const [activeWinner, setActiveWinner] = useState<{ entry: WheelEntry; index: number } | null>(null);

  const rotationRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const lastSegmentIndexRef = useRef<number>(-1);
  const isSpinningRef = useRef<boolean>(false);

  // Keep rotationRef in sync
  useEffect(() => {
    rotationRef.current = rotationDeg;
  }, [rotationDeg]);

  // Clean up RAF on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const spin = useCallback((): boolean => {
    if (entries.length < 2) {
      return false;
    }
    if (isSpinningRef.current || spinState === 'spinning' || spinState === 'stopping') {
      return false;
    }

    isSpinningRef.current = true;
    setSpinState('spinning');
    setActiveWinner(null);
    onSpinStart?.();

    const startRotation = rotationRef.current;
    // 1. Pick winner cryptographically
    const winnerIdx = secureRandomInt(0, entries.length - 1);
    const winningEntry = entries[winnerIdx];

    // 2. Calculate exact target rotation
    const spinDurationSec = Math.max(2, duration || 5);
    const target = calculateTargetRotation(startRotation, winnerIdx, entries.length, spinDurationSec);
    const targetRotation = target.targetRotationDeg;
    const totalRotationDelta = targetRotation - startRotation;

    const startTime = performance.now();
    const durationMs = spinDurationSec * 1000;

    let currentPointerDeflection = 0;
    lastSegmentIndexRef.current = getIndexUnderPointer(startRotation, entries.length);

    const step = (now: DOMHighResTimeStamp) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, Math.max(0, elapsed / durationMs));

      // Deceleration curve
      const easedProgress = easeOutCustom(progress);
      const currentRot = startRotation + totalRotationDelta * easedProgress;
      const velRatio = easeOutCustomVelocity(progress) / 4.2;

      rotationRef.current = currentRot;
      setRotationDeg(currentRot);

      // Detect segment crossing under pointer for tick sound and needle deflection
      const currentSegIndex = getIndexUnderPointer(currentRot, entries.length);
      if (currentSegIndex !== lastSegmentIndexRef.current && currentSegIndex !== -1) {
        lastSegmentIndexRef.current = currentSegIndex;
        onTick?.(velRatio);
        // Deflect pointer needle in direction of wheel rotation (counter-clockwise visual resistance)
        currentPointerDeflection = -Math.min(22, 10 + velRatio * 14);
      } else {
        // Spring back smoothly
        currentPointerDeflection *= 0.82;
      }

      setPointerDeflectionDeg(currentPointerDeflection);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      } else {
        // Spin finished!
        animationFrameRef.current = null;
        isSpinningRef.current = false;
        setRotationDeg(targetRotation);
        setPointerDeflectionDeg(0);
        setSpinState('celebrating');

        // Confirm winner
        const verifiedIdx = getIndexUnderPointer(targetRotation, entries.length);
        const resolvedWinner = entries[verifiedIdx] || winningEntry;

        setActiveWinner({ entry: resolvedWinner, index: verifiedIdx });
        onStop?.();
        onWinnerSelected?.(resolvedWinner, verifiedIdx);
      }
    };

    animationFrameRef.current = requestAnimationFrame(step);
    return true;
  }, [entries, duration, spinState, onSpinStart, onTick, onStop, onWinnerSelected]);

  const resetSpin = useCallback(() => {
    setSpinState('idle');
    setActiveWinner(null);
    setPointerDeflectionDeg(0);
    isSpinningRef.current = false;
  }, []);

  return {
    spinState,
    rotationDeg,
    pointerDeflectionDeg,
    activeWinner,
    isSpinning: spinState === 'spinning' || spinState === 'stopping',
    spin,
    resetSpin,
  };
}
